// client/src/components/UserWishlists.js

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import { jwtDecode } from 'jwt-decode';
import ProxiedImage from './ProxiedImage';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import {
  Box,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AppBarHeader from './AppBarHeader';

const UserWishlists = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wishlists, setWishlists] = useState({ 
    gameWishlist: [], 
    consoleWishlist: [], 
    legoWishlist: [],
    movieWishlist: [],
    musicWishlist: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get feature flags
  const { featureFlags } = useFeatureFlags();

  // Get current user info for AppBarHeader
  const token = localStorage.getItem('token');
  const userDecoded = token ? jwtDecode(token) : null;
  const userId = userDecoded ? userDecoded.id : null;
  const isAdmin = userDecoded ? userDecoded.isAdmin : false;

  const filterItems = (items, searchTerm) => {
    if (!searchTerm) return items;
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return items.filter(item => {
      const searchableFields = Object.values(item).map(value => 
        value ? String(value).toLowerCase() : ''
      );
      return searchableFields.some(field => field.includes(lowerSearchTerm));
    });
  };

  useEffect(() => {
    const fetchWishlists = async () => {
      try {
        console.log('Fetching wishlists for user:', id);
        
        // Fetch user profile first to get username
        const userResponse = await axiosInstance.get(`/users/${id}/profile`);
        setUsername(userResponse.data.username || 'User');

        // Fetch wishlists
        const response = await axiosInstance.get(`/users/${id}/wishlists`);
        console.log('Wishlists response:', response.data);
        
        // Fetch movie and music wishlists if feature flags are enabled
        let movieWishlist = [];
        let musicWishlist = [];
        
        if (featureFlags.enableMoviesFeature) {
          try {
            const movieResponse = await axiosInstance.get(`/movies/user/${id}?wishlist=true`);
            movieWishlist = movieResponse.data || [];
            console.log('Movie wishlist response:', movieWishlist);
          } catch (err) {
            console.error('Error fetching movie wishlist:', err);
          }
        }
        
        if (featureFlags.enableMusicFeature) {
          try {
            const musicResponse = await axiosInstance.get(`/music/user/${id}?wishlist=true`);
            musicWishlist = musicResponse.data || [];
            console.log('Music wishlist response:', musicWishlist);
          } catch (err) {
            console.error('Error fetching music wishlist:', err);
          }
        }

        setWishlists({
          gameWishlist: response.data.gameWishlist || [],
          consoleWishlist: response.data.consoleWishlist || [],
          legoWishlist: response.data.legoWishlist || [],
          movieWishlist: movieWishlist,
          musicWishlist: musicWishlist
        });
      } catch (err) {
        console.error('Detailed error:', err);
        setError('Failed to load wishlists.');
      } finally {
        setLoading(false);
      }
    };

    fetchWishlists();
  }, [id]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderGameWishlist = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search games..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Image</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Platform</TableCell>
            <TableCell>Release Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(wishlists.gameWishlist, searchTerm).length > 0 ? (
            filterItems(wishlists.gameWishlist, searchTerm).map((game) => (
              <TableRow key={game._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {game.imageUrl || game.background_image ? (
                      <ProxiedImage 
                        src={game.imageUrl || game.background_image} 
                        alt={game.name || game.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      (game.name || game.title || '?')[0]
                    )}
                  </Avatar>
                </TableCell>
                <TableCell>{game.name || game.title}</TableCell>
                <TableCell>{game.platform}</TableCell>
                <TableCell>{game.released}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                {searchTerm ? 'No games found matching your search' : 'No games in wishlist'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderConsoleWishlist = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search consoles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Logo</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Generation</TableCell>
            <TableCell>Alternative Name</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(wishlists.consoleWishlist, searchTerm).length > 0 ? (
            filterItems(wishlists.consoleWishlist, searchTerm).map((console) => (
              <TableRow key={console._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    src={console.platformLogo || console.imageUrl}
                    alt={console.name}
                    sx={{ width: 60, height: 60 }}
                  />
                </TableCell>
                <TableCell>{console.name}</TableCell>
                <TableCell>{console.generation}</TableCell>
                <TableCell>{console.alternative_name}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                {searchTerm ? 'No consoles found matching your search' : 'No consoles in wishlist'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderMovieWishlist = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search movies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Poster</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Director</TableCell>
            <TableCell>Year</TableCell>
            <TableCell>Format</TableCell>
            <TableCell>Priority</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(wishlists.movieWishlist, searchTerm).length > 0 ? (
            filterItems(wishlists.movieWishlist, searchTerm).map((movie) => (
              <TableRow key={movie._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {movie.imageUrl ? (
                      <ProxiedImage 
                        src={movie.imageUrl} 
                        alt={movie.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      (movie.title || '?')[0]
                    )}
                  </Avatar>
                </TableCell>
                <TableCell>{movie.title}</TableCell>
                <TableCell>{movie.director || 'Unknown'}</TableCell>
                <TableCell>{movie.year || 'Unknown'}</TableCell>
                <TableCell>
                  <Chip 
                    label={movie.format || 'Unknown'} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{movie.priority || 'Medium'}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                {searchTerm ? 'No movies found matching your search' : 'No movies in wishlist'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderMusicWishlist = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search music..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Cover</TableCell>
            <TableCell>Album</TableCell>
            <TableCell>Artist</TableCell>
            <TableCell>Year</TableCell>
            <TableCell>Format</TableCell>
            <TableCell>Priority</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(wishlists.musicWishlist, searchTerm).length > 0 ? (
            filterItems(wishlists.musicWishlist, searchTerm).map((album) => (
              <TableRow key={album._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {album.imageUrl ? (
                      <ProxiedImage 
                        src={album.imageUrl} 
                        alt={album.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      (album.name || '?')[0]
                    )}
                  </Avatar>
                </TableCell>
                <TableCell>{album.name}</TableCell>
                <TableCell>{album.artist}</TableCell>
                <TableCell>{album.year || 'Unknown'}</TableCell>
                <TableCell>
                  <Chip 
                    label={album.format || 'Unknown'} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{album.priority || 'Medium'}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                {searchTerm ? 'No music found matching your search' : 'No music in wishlist'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderLegoWishlist = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search LEGO sets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Image</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Set Number</TableCell>
            <TableCell>Theme</TableCell>
            <TableCell>Priority</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(wishlists.legoWishlist, searchTerm).length > 0 ? (
            filterItems(wishlists.legoWishlist, searchTerm).map((set) => (
              <TableRow key={set._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    src={set.imageUrl}
                    alt={set.name}
                    sx={{ width: 60, height: 60 }}
                  />
                </TableCell>
                <TableCell>{set.name}</TableCell>
                <TableCell>{set.setNumber ? set.setNumber.split('-')[0] : 'N/A'}</TableCell>
                <TableCell>{set.theme}</TableCell>
                <TableCell>{set.priority}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {searchTerm ? 'No LEGO sets found matching your search' : 'No LEGO sets in wishlist'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <AppBarHeader userId={userId} isAdmin={isAdmin} onLogout={handleLogout} />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {username}'s Wishlists
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="wishlist tabs">
            <Tab label="Games" />
            <Tab label="Consoles" />
            <Tab label="LEGO Sets" />
            {featureFlags.enableMoviesFeature && <Tab label="Movies" />}
            {featureFlags.enableMusicFeature && <Tab label="Music" />}
          </Tabs>
        </Box>
        {activeTab === 0 && renderGameWishlist()}
        {activeTab === 1 && renderConsoleWishlist()}
        {activeTab === 2 && renderLegoWishlist()}
        {featureFlags.enableMoviesFeature && activeTab === 3 && renderMovieWishlist()}
        {featureFlags.enableMusicFeature && activeTab === (featureFlags.enableMoviesFeature ? 4 : 3) && renderMusicWishlist()}
      </Container>
    </Box>
  );
};

export default UserWishlists;
